/**
 * Bank › Books (screens §2.1): one table per book family, built from `ProductLine[]`. A column is a function of the lines in a row, so a book (group)
 * row and a product row use the same arithmetic: sums add, rates weight by balance, and a field the product does not carry reads "—", never 0.
 */
import type { BookId, ProductLine } from "@bsim/domain";
import { formatMillionsBare, formatRate, formatUsdMWhole, formatYears } from "../format";
import type { TableRow, ColumnDef } from "./DataTable";
import { BOOK_LABELS } from "./statements";

export const BOOK_TAB_IDS = ["loans", "securities", "deposits", "funding"] as const;
export type BookTab = (typeof BOOK_TAB_IDS)[number];

type Lines = readonly ProductLine[];
type Num = (p: ProductLine) => number | undefined;
interface Column { readonly column: ColumnDef; readonly cell: (lines: Lines, tabTotalUsd: number) => string | null }

const sum = (lines: Lines, f: Num): number | null => {
  const seen = lines.flatMap((p) => { const v = f(p); return v === undefined ? [] : [v]; });
  return seen.length === 0 ? null : seen.reduce((a, b) => a + b, 0);
};
const balanceOf = (lines: Lines): number => lines.reduce((a, p) => a + p.balanceUsd, 0);
/** Balance-weighted mean of a rate over the lines that carry it. */
const weighted = (lines: Lines, f: Num): number | null => {
  const carried = lines.filter((p) => f(p) !== undefined);
  const base = balanceOf(carried);
  return carried.length === 0 || base === 0 ? null : carried.reduce((a, p) => a + (f(p) ?? 0) * p.balanceUsd, 0) / base;
};
const share = (part: number | null, whole: number): number | null => (part === null || whole === 0 ? null : part / whole);

/** One money format across the Bank page: whole millions. In a table the column header names the unit ("($M)"), so the cell is a bare number; in a sentence `usdM` writes "$4,400M". */
const usd = (v: number | null, opts: { readonly signed?: boolean } = {}): string | null => (v === null ? null : formatMillionsBare(v, opts));
const usdM = formatUsdMWhole;
export const pct = (v: number | null): string | null => (v === null ? null : formatRate(v, 1));
const pct2 = (v: number | null): string | null => (v === null ? null : formatRate(v, 2));
const years = (v: number | null): string | null => (v === null ? null : formatYears(v, { short: true }));
const reprice12 = (p: ProductLine): number => p.repricingUsd.m0to3 + p.repricingUsd.m3to12;

const balance: Column = { column: "Balance ($M)", cell: (l) => usd(balanceOf(l)) };
const loans: readonly Column[] = [
  balance,
  { column: { label: "Share of loans", wide: true }, cell: (l, t) => pct(share(balanceOf(l), t)) },
  { column: { label: "Yield", term: "yield" }, cell: (l) => pct2(weighted(l, (p) => p.yieldRate)) },
  { column: { label: "Fixed share", wide: true }, cell: (l) => pct(weighted(l, (p) => p.fixedShareRate)) },
  { column: { label: "Reprices ≤12m", term: "repricing" }, cell: (l) => pct(share(sum(l, reprice12), balanceOf(l))) },
  { column: "New lending ($M)", cell: (l) => usd(sum(l, (p) => p.originationsUsd)) },
  { column: { label: "Net charge-offs ($M)", term: "nco-rate" }, cell: (l) => usd(sum(l, (p) => p.ncoUsd)) },
  { column: { label: "Reserve ($M)", wide: true }, cell: (l) => usd(sum(l, (p) => p.aclUsd)) },
  { column: "Non-performing ($M)", cell: (l) => usd(sum(l, (p) => p.nonaccrualUsd)) },
];
const securities: readonly Column[] = [
  balance,
  { column: "Fair value ($M)", cell: (l) => usd(sum(l, (p) => p.fairValueUsd)) },
  { column: "Unrealised ($M)", cell: (l) => usd(sum(l, (p) => p.unrealizedUsd), { signed: true }) },
  { column: { label: "Accounting", term: "afs-htm" }, cell: (l) => { const a = new Set(l.map((p) => p.accounting)); return a.size === 1 && !a.has(undefined) ? (a.has("AFS") ? "Available for sale" : "Held to maturity") : null; } },
  { column: { label: "Duration", term: "duration" }, cell: (l) => years(weighted(l, (p) => p.durationYears)) },
  { column: { label: "Yield", term: "yield" }, cell: (l) => pct2(weighted(l, (p) => p.yieldRate)) },
  { column: "Matures ≤12m", cell: (l) => pct(share(sum(l, reprice12), balanceOf(l))) },
  { column: "Pledged ($M)", cell: (l) => usd(sum(l, (p) => p.pledgedUsd)) },
];
const deposits: readonly Column[] = [
  balance,
  { column: { label: "Share of deposits", wide: true }, cell: (l, t) => pct(share(balanceOf(l), t)) },
  { column: "Posted rate", cell: (l) => pct2(weighted(l, (p) => p.couponRate)) },
  { column: { label: "Beta", term: "deposit-beta" }, cell: (l) => pct(weighted(l, (p) => p.betaRate)) },
  { column: { label: "Realised beta", wide: true }, cell: (l) => pct(weighted(l, (p) => p.realizedBetaRate)) },
  { column: "Uninsured ($M)", cell: (l) => usd(sum(l, (p) => p.uninsuredUsd)) },
  { column: { label: "Uninsured share", term: "uninsured-share" }, cell: (l) => pct(share(sum(l, (p) => p.uninsuredUsd), balanceOf(l))) },
  { column: "Counted in the stress test", cell: (l) => { const v = l.flatMap((p) => (p.inRunScope === undefined ? [] : [p.inRunScope])); return v.length === 0 ? null : v.every(Boolean) ? "Yes" : v.some(Boolean) ? "Partly" : "No"; } },
  { column: { label: "Average life", wide: true }, cell: (l) => years(weighted(l, (p) => p.averageLifeYears)) },
  { column: "Net inflow ($M)", cell: (l) => usd(sum(l, (p) => p.netFlowUsd), { signed: true }) },
];
const funding: readonly Column[] = [
  balance,
  { column: "Rate", cell: (l) => pct2(weighted(l, (p) => p.couponRate)) },
  { column: "Matures ≤30d ($M)", cell: (l) => usd(sum(l, (p) => p.maturesUsd?.d0to30)) },
  { column: "Matures ≤12m ($M)", cell: (l) => usd(sum(l, (p) => p.maturesUsd?.m1to12)) },
  { column: "Matures later ($M)", cell: (l) => usd(sum(l, (p) => p.maturesUsd?.later)) },
  { column: { label: "Capacity left ($M)", term: "fhlb" }, cell: (l) => usd(sum(l, (p) => p.capacityUsd)) },
  { column: "Pledged ($M)", cell: (l) => usd(sum(l, (p) => p.pledgedUsd)) },
];

const UNIT = " ($M)";
/** A column read as a fact outside its table (a product header, a drawer): its name without the unit, and its figure with the unit written in ("4,400" under "Balance ($M)" is "$4,400M"). */
export function columnFact(c: { readonly column: ColumnDef; readonly cell: (lines: Lines, tabTotalUsd: number) => string | null }, lines: Lines, tabTotalUsd: number): { readonly label: string; readonly value: string } | null {
  const value = c.cell(lines, tabTotalUsd);
  if (value === null) return null;
  const name = typeof c.column === "string" ? c.column : c.column.label;
  return name.endsWith(UNIT) ? { label: name.slice(0, -UNIT.length), value: value.replace(/^([+\u2212-]?)(.*)$/, "$1$$$2M") } : { label: name, value };
}

/** `groups`: the rows' headers, each a set of books; `label` of the first column. */
export const BOOK_TABS: Readonly<Record<BookTab, { readonly label: string; readonly first: string; readonly total: string; readonly columns: readonly Column[]; readonly groups: readonly (readonly [id: string, label: string, books: readonly BookId[]])[] }>> = {
  loans: { label: "Loans", first: "Product", total: "Total loans", columns: loans, groups: [["ci", BOOK_LABELS["ci"] ?? "", ["ci"]], ["cre", BOOK_LABELS["cre"] ?? "", ["cre"]], ["consumer", "Mortgages and consumer", ["resi", "consumer", "card"]]] },
  securities: { label: "Securities", first: "Product", total: "Total securities", columns: securities, groups: [["ust_afs", "US Treasuries", ["ust_afs"]], ["mbs_afs", "Mortgage bonds (available for sale)", ["mbs_afs"]], ["mbs_htm", "Mortgage bonds (held to maturity)", ["mbs_htm"]]] },
  deposits: { label: "Deposits", first: "Product", total: "Total, excluding brokered CDs", columns: deposits, groups: [["dda", BOOK_LABELS["dda"] ?? "", ["dda"]], ["int_checking", "Interest checking", ["int_checking"]], ["mmda", "Money market", ["mmda"]], ["time", BOOK_LABELS["time"] ?? "", ["time"]]] },
  funding: { label: "Funding", first: "Source", total: "Total funding", columns: funding, groups: [["fhlb", BOOK_LABELS["fhlb"] ?? "", ["fhlb"]], ["brokered", BOOK_LABELS["brokered"] ?? "", ["brokered"]], ["ffp", "Fed funds purchased", ["ffp"]], ["dw", "Discount window", ["dw"]], ["sub_debt", "Subordinated debt", ["sub_debt"]]] },
};

export const booksOf = (tab: BookTab): ReadonlySet<BookId> => new Set(BOOK_TABS[tab].groups.flatMap(([, , books]) => books));
export const tabLines = (tab: BookTab, products: Lines): Lines => products.filter((p) => booksOf(tab).has(p.bookId));

/** The tab's rows: a header row per book (its total, open) over its products, then the tab total. A single-product book is its own line. */
export function bookRows(tab: BookTab, products: Lines): TableRow[] {
  const { columns, groups, total } = BOOK_TABS[tab];
  const all = tabLines(tab, products);
  const tabTotal = balanceOf(all);
  const cells = (lines: Lines) => columns.map((c) => c.cell(lines, tabTotal));
  const rows = groups.flatMap(([id, label, books]): TableRow[] => {
    const lines = all.filter((p) => books.includes(p.bookId));
    if (lines.length === 0) return [];
    const [only] = lines;
    if (lines.length === 1 && only !== undefined) return [{ id: only.productId, label: only.label, cells: cells(lines) }];
    return [{ id, label, group: true, open: true, cells: cells(lines), children: lines.map((p) => ({ id: p.productId, label: p.label, cells: cells([p]) })) }];
  });
  return all.length === 0 ? [] : [...rows, { id: "total", label: total, total: true, cells: cells(all) }];
}

export interface MonthlyBooks { readonly month: number; readonly products: readonly { readonly productId: string; readonly balanceUsd: number; readonly couponRate: number }[] }

/** History view: one column per month, each cell the balance and the rate it carried. Rows follow the same books as the period view. */
export function historyRows(tab: BookTab, products: Lines, monthly: readonly MonthlyBooks[]): TableRow[] {
  const cells = (ids: ReadonlySet<string>) => monthly.map((m) => {
    const at = m.products.filter((p) => ids.has(p.productId));
    const balance = at.reduce((a, p) => a + p.balanceUsd, 0);
    return at.length === 0 ? null : `${usdM(balance)} · ${pct2(balance === 0 ? 0 : at.reduce((a, p) => a + p.couponRate * p.balanceUsd, 0) / balance)}`;
  });
  const all = tabLines(tab, products);
  return BOOK_TABS[tab].groups.flatMap(([id, label, books]): TableRow[] => {
    const lines = all.filter((p) => books.includes(p.bookId));
    const [only] = lines;
    if (lines.length === 1 && only !== undefined) return [{ id: only.productId, label: only.label, cells: cells(new Set([only.productId])) }];
    return lines.length === 0 ? [] : [{ id, label, group: true, open: true, cells: cells(new Set(lines.map((p) => p.productId))), children: lines.map((p) => ({ id: p.productId, label: p.label, cells: cells(new Set([p.productId])) })) }];
  });
}

/**
 * The period view without the columns no row carries a figure for (F16.5 I): a flow the engine did not send for the period (new lending, deposit flows) is left out whole,
 * never a column of dashes. The columns and each row's cells (and its children's) are cut together.
 */
export function withoutEmptyColumns(columns: readonly ColumnDef[], rows: readonly TableRow[]): { readonly columns: readonly ColumnDef[]; readonly rows: readonly TableRow[] } {
  const filled = (r: TableRow, i: number): boolean => r.cells[i] != null || (r.children ?? []).some((c) => filled(c, i));
  const keep = columns.map((_, i) => rows.some((r) => filled(r, i)));
  const cut = (r: TableRow): TableRow => ({ ...r, cells: r.cells.filter((_, i) => keep[i] !== false), ...(r.children === undefined ? {} : { children: r.children.map(cut) }) });
  return { columns: columns.filter((_, i) => keep[i]), rows: rows.map(cut) };
}

export const bookColumns = (tab: BookTab): readonly ColumnDef[] => BOOK_TABS[tab].columns.map((c) => c.column);

/** Loans footer (screens §2.1): fixed, floating and within-a-year shares of the loan book. */
export function loanFooter(products: Lines): string | null {
  const lines = tabLines("loans", products);
  const fixed = weighted(lines, (p) => p.fixedShareRate);
  const within = share(sum(lines, reprice12), balanceOf(lines));
  return fixed === null || within === null ? null : `Loans ${usdM(balanceOf(lines))} · fixed ${pct(fixed)} · floating ${pct(1 - fixed)} · reprices within a year ${pct(within)}`;
}

/** Securities footer: the unrealised AFS mark (before tax) beside the "Bond marks in equity" line and the held-to-maturity mark. */
export function securitiesFooter(products: Lines, aociAfsUsd: number): string {
  const afs = tabLines("securities", products).filter((p) => p.accounting === "AFS");
  const htm = tabLines("securities", products).filter((p) => p.accounting === "HTM");
  return `Unrealised on bonds available for sale ${usdM(sum(afs, (p) => p.unrealizedUsd) ?? 0, { signed: true })} before tax; bond losses counted in equity ${usdM(aociAfsUsd, { signed: true })} after tax. Held-to-maturity mark ${usdM(sum(htm, (p) => p.unrealizedUsd) ?? 0, { signed: true })} (see Capital and marks).`;
}

/** Deposits footer: brokered CDs sit under Funding here but in Deposits on the balance sheet, so the two totals differ by exactly that line. */
export function depositsFooter(products: Lines): string | null {
  const brokered = products.filter((p) => p.bookId === "brokered").reduce((n, p) => n + p.balanceUsd, 0);
  const own = sum(tabLines("deposits", products), (p) => p.balanceUsd);
  return own === null || brokered === 0 ? null : `Brokered certificates of deposit (${usdM(brokered)}) are listed under Funding. The balance sheet counts them as deposits, so its deposits line is ${usdM(own + brokered)}.`;
}

/** The funding order, in words (06.2 E2–E6; no equation ids). */
export const FUNDING_ORDER = "When cash falls below its floor the bank borrows from the home-loan bank (FHLB) first, then brokered deposits, the discount window and fed funds, and sells bonds last.";

export const fundingFooter = (wholesaleShare: number | undefined): string =>
  `${wholesaleShare === undefined ? "" : `Wholesale funding ${pct(wholesaleShare)} of liabilities. `}${FUNDING_ORDER}`;

/** A line's repricing ladder, as label and amount. */
export const LADDER: readonly (readonly [keyof ProductLine["repricingUsd"], string])[] = [
  ["m0to3", "Within 3 months"], ["m3to12", "3 to 12 months"], ["y1to3", "1 to 3 years"], ["y3to5", "3 to 5 years"], ["y5plus", "Over 5 years"], ["nonSensitive", "Does not reprice"],
];
